import React, { useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { CardActionArea } from '@mui/material';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
// import ButtonGroup from '@mui/material/ButtonGroup';
// import Button from '@mui/material/Button';
// import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { Link } from 'react-router-dom';

import './styles.css'

import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';

import { trimSentence } from '../../../utils';
import { formatMoney, defaultPriceUnitFor } from '../../../utils/pricing';
import { scrollableTabsSx } from '../../../theme';

/**
 * Abbreviated unit labels. The full wording lives in PRICE_UNITS; a card
 * has room for a suffix, not a sentence. This card previously hardcoded
 * "$" and "pp" regardless of what the listing actually said, so a ZWG
 * per-night listing read as "$90 pp" here and "ZWG 90.00 per night" in
 * the booking dialog.
 */
const UNIT_SUFFIX = {
  PER_NIGHT: '/night',
  PER_PERSON: 'pp',
  PER_PERSON_PER_NIGHT: 'pp/night',
  PER_GROUP: 'total',
};

const TabCardContent = styled(CardContent)({
  padding: ".4rem",
  maxHeight: '5rem'
  // '& .MuiTabs-indicator': {
  //   backgroundColor: '#1890ff',
  // },
});

const StyledTabs = styled((props) => (
  <Tabs
    {...props}
    slotProps={{ indicator: { children: <span className="MuiTabs-indicatorSpan" /> } }}
  />
))({
  // Keeps the first pill flush with the section heading on rows that overflow.
  ...scrollableTabsSx,
  '& .MuiTabs-root': {
    minHeight: 0,
    maxHeight: "4rem"
  },
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  '& .MuiTabs-indicatorSpan': {
    // maxWidth: 40,
    width: '100%',
    backgroundColor: 'transparent',
  },
});

// -MuiTabs-root {
const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    fontWeight: 500,
    fontSize: theme.typography.pxToRem(14),
    marginRight: theme.spacing(1),
    minHeight: 0,
    height: '2rem',
    minWidth: 'auto',
    padding: '0 14px',
    borderRadius: 999,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    // Active state intentionally left as the brand orange pill — the
    // white-on-orange contrast is a pending brand decision (see CHECKPOINT_REPORT).
    '&.Mui-selected': {
      color: '#fff',
      backgroundColor: 'rgba(255, 118, 86,1)',
      borderColor: 'transparent',
    },
    '&.Mui-focusVisible': {
      backgroundColor: theme.palette.action.hover,
    },
  }),
);

// Because this is an inframe, so the SSR mode doesn't not do well here.
// It will work on real devices.
const CardSection = ({ title, filters, serviceProviders }) => {
  const [activeFilter, setActiveFilter] = useState(null);
  const [screenSize, setScreenSize] = useState(getCurrentDimension());

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  function getCurrentDimension() {
    return {
      width: window.innerWidth,
      height: window.innerHeight
    }
  }

  useEffect(() => {
    const updateDimension = () => {
      setScreenSize(getCurrentDimension())
    }
    window.addEventListener('resize', updateDimension);


    return (() => {
      window.removeEventListener('resize', updateDimension);
    })
  }, [screenSize])

  // Derived from the prop rather than copied into state on mount. The copy
  // this replaces used an empty dependency array, so the section rendered
  // whatever arrived with the first onSnapshot and ignored every update
  // after it — a listing published, unpublished or repriced by a provider
  // did not appear until the traveller reloaded the page.
  const providersList = useMemo(
    () => (Array.isArray(serviceProviders) ? serviceProviders : []),
    [serviceProviders]
  );

  // Deriving the filtered view rather than storing it keeps the active
  // sub-filter applied across those updates instead of resetting to "All".
  const filteredProvidersList = useMemo(() => {
    if (!activeFilter) return providersList;
    // A listing saved before sub-filters became a fixed list may carry no
    // `filters` array at all; it matches nothing rather than throwing.
    return providersList.filter(
      (item) => Array.isArray(item.filters) && item.filters.includes(activeFilter)
    );
  }, [providersList, activeFilter]);

  const applyFilter = (filter) => setActiveFilter(filter);

  return (
    <Box sx={{ margin: '1rem 0 0 1em' }} className='home-section'>
      <Typography component="div" className='txt--gray'>
        {title}
      </Typography>
      <Box sx={{ maxWidth: '100%' }}>
        <StyledTabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Category Filters"
        >
          <StyledTab label="All" onClick={() => applyFilter(null)} />
          {
            filters && filters.length > 0 && filters.map(filter => {
              return <StyledTab key={`${filter}-home-card`} label={filter} onClick={() => applyFilter(filter)} />
            })
          }
        </StyledTabs>
      </Box>
      <Box sx={{ width: '100%' }} className='home-destinations'>
        {filteredProvidersList && filteredProvidersList.map((provider, i) => {
          return (
            <Box
              component="div"
              key={`filter-${i}`}
            >
              {/* A real anchor, not an onClick. The card used to open a modal,
                  so hover showed no destination and middle-click / open-in-new-tab
                  did nothing — on a travel site where people compare places in
                  several tabs. CardActionArea drops to a div so the anchor is not
                  wrapping a button. */}
              <Card
                className='home-destinations-card'
                component={Link}
                to={`/listing/${provider.id}`}
                sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
              >
                <CardActionArea component="div">
                  <CardMedia
                    className='home-destinations-img'
                    component="img"
                    sx={{ maxHeight: '30rem' }}
                    image={provider.images?.[0] || ''}
                    alt={provider.name}
                  />
                  <div className="card-price-scrim"></div>
                  <div>
                    <div style={{ position: 'absolute', bottom: 0, left: 0 }}>
                      <Typography
                        variant="h6"
                        component="div"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginLeft: '.4rem',
                          marginBottom: '0px',
                          display: 'flex',
                          width: '50px',
                          height: 'auto',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flexShrink: 0,
                          color: '#FFF',
                          textShadow: '0px 4px 4px #000',
                          fontFamily: 'Montserrat',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '80%'
                        }
                        }
                      >From</Typography>
                      <Typography
                        variant="h6"
                        component="div"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginLeft: '.4rem',
                          display: 'flex',
                          height: 'auto',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          flexShrink: 0,
                          color: '#FFF',
                          textShadow: '0px 4px 4px #000',
                          fontFamily: 'Montserrat',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          lineHeight: '135%',
                          // Was declared twice; '0px' earlier in this object was
                          // silently discarded by this one.
                          marginBottom: '.5rem'
                        }
                        }>{formatMoney(provider.cost, provider.currency)} <i style={{ fontSize: ".7rem", marginLeft: ".2rem" }}> {UNIT_SUFFIX[provider.priceUnit || defaultPriceUnitFor(provider.type)] || 'pp'}</i>
                      </Typography>

                    </div>

                  </div>
                </CardActionArea>
                <TabCardContent>
                  <Typography variant="h6" component="div"
                    sx={{ textWrap: "nowrap", maxHeight: '1.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'wrap', fontFamily: 'montserrat', fontSize: '.8em', lineHeight: '1.1em', display: 'flex', alignItems: 'start', color: 'text.primary', fontWeight: '600' }}>
                    {/* {provider.name} */}
                    {provider?.name?.length > 20 ? `${trimSentence(provider.name, 20)}` : (provider?.name || "")}

                  </Typography>
                  <Typography variant="h6" component="div"
                    sx={{ margin: ".25rem 0", whiteSpace: 'wrap', fontFamily: 'montserrat', fontSize: '.8em', lineHeight: '1.1em', display: 'flex', alignItems: 'center', marginBottom: '-18px', overflow: 'hidden', color: 'text.secondary' }}>
                    <LocationOnRoundedIcon sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary', flexShrink: 0 }} /> {provider?.location?.length > 15 ? `${trimSentence(provider.location, 15)}...` : (provider?.location || "")}
                    {provider?.booked ? <CalendarMonthRoundedIcon sx={{ fontSize: '1rem', ml: 0.9, color: 'text.primary', flexShrink: 0 }} /> : ''}
                  </Typography>
                </TabCardContent>
              </Card>

            </Box>
          )
        })}
      </Box>
    </Box>
  );
};

export default CardSection;
